import React from "react";
import type { RenderSiteProps } from "./RenderSite";
import { detectSocialPlatform, getSocialIcon } from "../helpers/socialLinks";
import { I18n } from "../i18n";
import { isHttpUrl } from "./externalLinkProps";

const SocialLink: React.FC<{ url: string }> = ({ url }) => {
  if (!isHttpUrl(url)) {
    return null;
  }
  const platform = detectSocialPlatform(url);
  const Icon = getSocialIcon(platform);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-600 hover:text-gray-900"
    >
      <Icon className="w-5 h-5" />
    </a>
  );
};

export const About: React.FC<RenderSiteProps> = ({ site }) => {
  const { teams } = site;
  const i18n = I18n(site.language);
  if (!teams?.is_enabled) {
    return null;
  }

  return (
    <section id="about" className="py-16 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-4 first-letter:uppercase">
            {teams.title || i18n["About us"]}
          </h2>
        </div>
        {teams.description && (
          <div className="mb-12 text-center">
            <p className="text-gray-700 max-w-3xl mx-auto first-letter:uppercase">
              {teams.description}
            </p>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-8">
          {teams.items?.map((member) => {
            return (
              <div key={member.id} className="text-center">
                {member.photo_uri && (
                  <img
                    width={member.image_metadata?.width}
                    height={member.image_metadata?.height}
                    src={member.photo_uri}
                    alt={member.name}
                    className="w-32 h-32 mx-auto rounded-full mb-4 shadow-lg object-cover"
                  />
                )}
                <h3 className="text-xl font-semibold text-gray-800 first-letter:uppercase">
                  {member.name}
                </h3>
                <div className="flex justify-center gap-3 mt-2">
                  {member.social_links?.map((link) => (
                    <SocialLink key={link.id} url={link.url} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
